'use client';

import { useState, MouseEvent } from 'react';
import { Tooltip, IconButton, TooltipProps } from '@mui/material';
import { usePhrase } from '@/hooks/general/usePhrase/usePhrase';
import { isEmptyPhrase } from '@/utils/phrases/isEmptyPhrase';
import { GENERAL_PHRASES } from '@/consts/phrases/generalPhrases';
import { config } from '@/config';

import InfoIcon from '@mui/icons-material/Info';
import InfoOutlineIcon from '@mui/icons-material/InfoOutlined';

export interface InfoTooltipProps extends Omit<TooltipProps, 'title' | 'children'> {
    title?: string | number;
}

export const InfoTooltip = ({ title, ...props }: InfoTooltipProps) => {
    const { t } = usePhrase();

    const [isTooltipOpened, setIsTooltipOpened] = useState(false);

    const handleTooltipToggle = (evt: MouseEvent<HTMLButtonElement>) => {
        evt.stopPropagation();
        setIsTooltipOpened((prev) => !prev);
    };

    const handleTooltipOpen = () => setIsTooltipOpened(true);
    const handleTooltipClose = () => setIsTooltipOpened(false);

    if (
        title === GENERAL_PHRASES.PHONE_INFO_TOOLTIP &&
        config.appEnv !== 'local' &&
        window?.__ENV?.NEXT_PUBLIC_PHONE_INFO_TOOLTIP_FEATURE !== 'true'
    ) {
        return null;
    }

    const titleText = typeof title === 'string' ? title : t(title);

    if (!titleText || isEmptyPhrase(titleText.toString())) return null;

    return (
        <Tooltip title={titleText} placement="bottom-end" {...props} open={isTooltipOpened}>
            <IconButton
                aria-label="additional info"
                size="small"
                sx={{
                    color: (theme) => theme.palette.info.main,
                    position: 'relative',
                    zIndex: 10,
                    cursor: 'default',
                }}
                className="infoTooltipIcon"
                onMouseEnter={handleTooltipOpen}
                onMouseLeave={handleTooltipClose}
                onClick={handleTooltipToggle}
                disableRipple
            >
                {isTooltipOpened ? (
                    <InfoIcon fontSize="small" />
                ) : (
                    <InfoOutlineIcon fontSize="small" />
                )}
            </IconButton>
        </Tooltip>
    );
};
